/**
 * Скрипт валидации форм.
 * Валидация осуществляется через плагин Parsley.
 *
 * @module Validation
 *
 * @see {@link http://parsleyjs.org/}
 */

import Parsley from 'parsleyjs';
import '../styles/validation-error.css';
import { rNameAll, rNameRu, rNameEn, rPassword, rTel, rKpp, rDate, rDateMatch } from './regex';
import { countCheckdigit, lang } from './helpers';
import Datepicker from '../components/base/Datepicker';

/* Сообщения для русской локали настраиваются здесь, так что импорт файла локализации излишен. Для сообщений английской локали значения берутся по умолчанию. */
Parsley.addMessages('ru', {
    defaultMessage: 'Некорректное значение',
    type: {
        email: 'Формат электронной почты: name@domain.сom',
        url: 'Формат URL адреса: http://www.example.com',
        number: 'Только числа',
        integer: 'Только целые числа',
        digits: 'Только цифры',
        alphanum: 'Только латинские буквы и числа',
    },
    notblank: 'Это поле не должно быть пустым',
    required: 'Обязательное поле',
    pattern: 'Это значение не удовлетворяет шаблону',
    min: 'Это значение должно быть не менее чем %s',
    max: 'Это значение должно быть не более чем %s',
    range: 'Это значение должно быть от %s до %s',
    minlength: 'Это значение должно содержать не менее %s символов',
    maxlength: 'Это значение должно содержать не более %s символов',
    length: 'Это значение должно содержать от %s до %s символов',
    mincheck: 'Выберите не менее %s значений',
    maxcheck: 'Выберите не более %s значений',
    check: 'Выберите от %s до %s значений',
    equalto: 'Это значение должно совпадать с предыдущим',
});

const locale = lang() === 'ru' ? 'ru' : 'en';

Parsley.setLocale(locale);

Object.assign(Parsley.options, {
    trigger: 'change',
    /* Включает валидацию вне зависимости от числа набранных символов (по умолчанию 3). */
    validationThreshold: '0',
    errorsWrapper: '<p class="validation-error"></p>',
    errorTemplate: '<span></span>',
    /**
     * Меняет размещение класса parsley-error.
     *
     * @param {Object} instance Параметры плагина.
     * @return {Object} Новый контейнер.
     */
    classHandler(instance) {
        const { $element } = instance;

        let $handler;

        /* Для селекта перемещаем класс со скрытого селекта наверх, чтобы стилизовать поле при возникновении ошибки. */
        if ($element.hasClass('select__input')) {
            $handler = $element.closest('.choices__inner');
        }

        return $handler;
    },
    /**
     * Меняет расположение сообщений с ошибками для специфичных элементов.
     *
     * @param {Object} instance Параметры плагина.
     * @return {Object} Новый контейнер.
     */
    errorsContainer(instance) {
        const { $element } = instance;

        let $container;

        if ($element.closest('.select').length) {
            $container = $element.closest('.select');
        } else if ($element.closest('.radio').length) {
            $container = $element.closest('.radio');
        } else if ($element.closest('.checkbox').length) {
            $container = $element.closest('.checkbox');
        } else if ($element.closest('.toggle').length) {
            $container = $element.closest('.toggle');
        } else if ($element.closest('.custom-file').length) {
            $container = $element.closest('.custom-file');
        }

        return $container;
    },
});

/* Кастомные валидаторы. */

Parsley.addValidator('name', {
    /**
     * Проверяет на латинские и русские буквы, тире и пробелы.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rNameAll.test(value);
    },
    messages: {
        ru: 'Только латинские и русские буквы, тире и пробелы',
        en: 'Only symbols A-Z, a-z, А-Я, а-я, " ", "-"',
    },
});

Parsley.addValidator('nameRu', {
    /**
     * Проверяет на русские буквы, тире и пробелы.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rNameRu.test(value);
    },
    messages: {
        ru: 'Только русские буквы, тире и пробелы',
        en: 'Only symbols А-Я, а-я, " ", "-"',
    },
});

Parsley.addValidator('nameEn', {
    /**
     * Проверяет на латинские буквы, тире, пробелы.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rNameEn.test(value);
    },
    messages: {
        ru: 'Только латинские буквы, тире и пробелы',
        en: 'Only symbols A-Z, a-z, " ", "-"',
    },
});

Parsley.addValidator('password', {
    /**
     * Проверяет на формат пароля.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rPassword.test(value);
    },
    messages: {
        ru: 'Пароль должен содержать как минимум 1 заглавную и строчную латинские буквы и 1 цифру',
        en: 'Password must contain at least 1 uppercase letter, 1 lowercase letter, and 1 number',
    },
});

Parsley.addValidator('tel', {
    /**
     * Проверяет на формат телефонного номера.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rTel.test(value);
    },
    messages: {
        ru: 'Формат телефонного номера: +7(000) 000-00-00',
        en: 'Phone number format: +7(000) 000-00-00',
    },
});

Parsley.addValidator('kpp', {
    /**
     * Проверяет на формат КПП.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rKpp.test(value);
    },
    messages: {
        ru: 'Некорректный формат КПП',
        en: 'Incorrect KPP format',
    },
});

Parsley.addValidator('inn', {
    /**
     * Проверяет на формат ИНН.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        const inn = value;

        let isCorrect = false;
        let checkdigit;
        let firstCheckdigit;
        let secondCheckdigit;

        /* ИНН может быть 10 или 12-значным и в каждом случае имеет свою логику проверки */
        switch (inn.length) {
            case 10:
                checkdigit = countCheckdigit(inn, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
                if (checkdigit === parseInt(inn[9], 10)) {
                    isCorrect = true;
                }
                break;
            case 12:
                firstCheckdigit = countCheckdigit(inn, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
                secondCheckdigit = countCheckdigit(inn, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
                if (firstCheckdigit === parseInt(inn[10], 10) && secondCheckdigit === parseInt(inn[11], 10)) {
                    isCorrect = true;
                }
                break;
            default:
        }

        return isCorrect;
    },
    messages: {
        ru: 'Некорректный формат ИНН',
        en: 'Incorrect INN format',
    },
});

Parsley.addValidator('dateSingle', {
    /**
     * Проверяет одиночную дату на корректность.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        return rDate.test(value);
    },
    /* Приоритет этого валидатора должен быть больше, чем у валидаторов dateMin/dateMax/dateMinMax */
    priority: 3,
    messages: {
        ru: 'Некорректная дата',
        en: 'Incorrect date',
    },
});

Parsley.addValidator('dateRange', {
    /**
     * Проверяет диапазон дат на корректность.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        /* Сохраняем строковые значения в массиве для удобства доступа */
        const values = value.split(' — ');

        return rDate.test(values[0]) && rDate.test(values[1]);
    },
    /* Приоритет этого валидатора должен быть больше, чем у валидаторов dateOrder/dateMin/dateMax/dateMinMax */
    priority: 3,
    messages: {
        ru: 'Некорректный диапазон дат',
        en: 'Incorrect date range',
    },
});

Parsley.addValidator('dateMultiple', {
    /**
     * Проверяет набор дат на корректность.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        /* Сохраняем строковые значения в массиве для удобства доступа */
        const values = value.split(', ');

        return values.every(val => rDate.test(val));
    },
    /* Приоритет этого валидатора должен быть больше, чем у валидаторов dateMin/dateMax/dateMinMax */
    priority: 3,
    messages: {
        ru: 'Некорректный набор дат',
        en: 'Incorrect date range',
    },
});

Parsley.addValidator('dateOrder', {
    /**
     * Проверяет диапазон дат на порядок следования.
     *
     * @param {string} value Проверяемое значение.
     * @return {boolean} Результат проверки.
     */
    validateString(value) {
        let datesArr = value.split(' — ');
        let result;

        datesArr = datesArr.map(date => {
            result = date.match(rDateMatch);
            return new Date(+result[3], result[2] - 1, +result[1]);
        });

        return datesArr[1] >= datesArr[0];
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла валидация dateRange */
    priority: 2,
    messages: {
        ru: 'Некорректный порядок дат',
        en: 'Incorrect date range',
    },
});

Parsley.addValidator('dateMaxCount', {
    /**
     * Проверяет набор дат на максимальное количество.
     *
     * @param {string} value Проверяемое значение.
     * @param {string} maxCount Максимальное количество дат.
     * @return {boolean} Результат проверки.
     */
    validateString(value, maxCount) {
        const datesArr = value.split(', ');

        return datesArr.length <= maxCount;
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла валидация dateMultiple */
    priority: 2,
    messages: {
        ru: 'Количество дат не должно превышать %s',
        en: 'Dates count should be less than %s',
    },
});

Parsley.addValidator('dateMinMaxCount', {
    /**
     * Проверяет набор дат на минимальное и максимальное количество.
     *
     * @param {string} value Проверяемое значение.
     * @param {string} count Количество дат.
     * @return {boolean} Результат проверки.
     */
    validateString(value, count) {
        const datesArr = value.split(', ');

        /* Значение атрибута всегда пишется в формате 'min — max', так что можно спокойно разбивать его на два */
        const countArr = count.split(' — ');
        const minCount = countArr[0];
        const maxCount = countArr[1];

        return datesArr.length >= minCount && datesArr.length <= maxCount;
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла валидация dateMultiple */
    priority: 2,
    messages: {
        ru: 'Количество дат должно лежать в диапазоне %s',
        en: 'Dates count should be in the range %s',
    },
});

Parsley.addValidator('dateMin', {
    /**
     * Проверяет дату на минимальное значение.
     *
     * @param {string} value Проверяемое значение.
     * @param {string} min Значение минимума.
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, min, instance) {
        const datepickerObj = Datepicker.getInstance(instance.$element.closest('.datepicker'));
        const isMultiple = datepickerObj.mode === 'multiple';
        const isRange = datepickerObj.mode === 'range';

        let datesArr = [];
        let result;

        /* Этот валидатор отрабатывает, только если успешно прошла проверка на корректность.
        Так что дополнительные проверки здесь не требуются */
        if (isMultiple) {
            datesArr = value.split(', ');
        } else if (isRange) {
            [datesArr[0]] = value.split(' — ');
        } else {
            datesArr[0] = value;
        }

        datesArr = datesArr.map(date => {
            result = date.match(rDateMatch);
            return new Date(+result[3], result[2] - 1, +result[1]);
        });

        let minDate;

        if (min !== 'today') {
            result = min.match(rDateMatch);
            minDate = new Date(+result[3], result[2] - 1, +result[1]);
        } else {
            minDate = new Date();
        }

        return datesArr.every(date => date >= minDate);
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла одна из валидаций dateSingle/dateRange/dateMultiple/dateOrder */
    priority: 1,
    messages: {
        ru: 'Дата должна быть не менее чем %s',
        en: 'Date should be greater than or equal to %s',
    },
});

Parsley.addValidator('dateMax', {
    /**
     * Проверяет дату на максимальное значение.
     *
     * @param {string} value Проверяемое значение.
     * @param {string} max Значение максимума.
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, max, instance) {
        const datepickerObj = Datepicker.getInstance(instance.$element.closest('.datepicker'));
        const isMultiple = datepickerObj.mode === 'multiple';
        const isRange = datepickerObj.mode === 'range';

        let datesArr = [];
        let result;

        /* Этот валидатор отрабатывает, только если успешно прошла проверка на корректность.
        Так что дополнительные проверки здесь не требуются */
        if (isMultiple) {
            datesArr = value.split(', ');
        } else if (isRange) {
            [, datesArr[0]] = value.split(' — ');
        } else {
            datesArr[0] = value;
        }

        datesArr = datesArr.map(date => {
            result = date.match(rDateMatch);
            return new Date(+result[3], result[2] - 1, +result[1]);
        });

        let maxDate;

        if (max !== 'today') {
            result = max.match(rDateMatch);
            maxDate = new Date(+result[3], result[2] - 1, +result[1]);
        } else {
            maxDate = new Date();
        }

        return datesArr.every(date => date <= maxDate);
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла одна из валидаций dateSingle/dateRange/dateMultiple/dateOrder */
    priority: 1,
    messages: {
        ru: 'Дата должна быть не более чем %s',
        en: 'Date should be lower than or equal to %s',
    },
});

Parsley.addValidator('dateMinMax', {
    /**
     * Проверяет дату на минимальное и максимальное значение.
     *
     * @param {string} value Проверяемое значение.
     * @param {string} range Значения минимума и максимума.
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, range, instance) {
        const datepickerObj = Datepicker.getInstance(instance.$element.closest('.datepicker'));
        const isMultiple = datepickerObj.mode === 'multiple';
        const isRange = datepickerObj.mode === 'range';

        let datesArr = [];
        let result;

        /* Этот валидатор отрабатывает, только если успешно прошла проверка на корректность. Так что дополнительные проверки здесь не требуются */
        if (isMultiple) {
            datesArr = value.split(', ');
        } else if (isRange) {
            datesArr = value.split(' — ');
        } else {
            datesArr[0] = value;
        }

        datesArr = datesArr.map(date => {
            result = date.match(rDateMatch);
            return new Date(+result[3], result[2] - 1, +result[1]);
        });

        /* Значение атрибута всегда пишется в формате 'min — max', так что можно спокойно разбивать его на два */
        const rangeArr = range.split(' — ');
        const min = rangeArr[0];
        const max = rangeArr[1];

        let minDate;
        let maxDate;

        if (min !== 'today') {
            result = min.match(rDateMatch);
            minDate = new Date(+result[3], result[2] - 1, +result[1]);
        } else {
            minDate = new Date();
        }

        if (max !== 'today') {
            result = max.match(rDateMatch);
            maxDate = new Date(+result[3], result[2] - 1, +result[1]);
        } else {
            maxDate = new Date();
        }

        return datesArr.every(date => date >= minDate && date <= maxDate);
    },
    /* Уменьшаем приоритет валидатора, чтобы он не вызывался, если не прошла одна из валидаций dateSingle/dateRange/dateMultiple/dateOrder */
    priority: 1,
    messages: {
        ru: 'Дата должна лежать в диапазоне %s',
        en: 'Date should be in the range %s',
    },
});

Parsley.addValidator('fileMaxCount', {
    /**
     * Проверяет на допустимость количество загружаемых файлов.
     *
     * @param {string} value Название файла (не используется).
     * @param {number} maxCount Максимально допустимое количество.
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, maxCount, instance) {
        const filesData = instance.$element[0].files;

        return filesData.length <= maxCount;
    },
    messages: {
        ru: 'Максимальное количество файлов: %s',
        en: "Files count can't be more than %s",
    },
});

Parsley.addValidator('fileMaxSize', {
    /**
     * Проверяет на допустимость размер загружаемого файла.
     *
     * @param {string} value Название файла (не используется).
     * @param {number} maxSize Максимально допустимый размер (в Кб).
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, maxSize, instance) {
        const filesData = instance.$element[0].files;

        /* filesData - это FileList, а не Array, так что привязываем every через call */
        const isCorrect = Array.prototype.every.call(filesData, fileData => fileData.size <= maxSize * 1024);

        return isCorrect;
    },
    messages: {
        ru: 'Размер файла не должен превышать %s Кб',
        en: "File size can't be more than %s Kb",
    },
});

Parsley.addValidator('fileExtensions', {
    /**
     * Проверяет на допустимость расширений загружаемого файла.
     *
     * @param {string} value Название файла (не используется).
     * @param {string} extensions Допустимые расширения (MIME types).
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, extensions, instance) {
        const filesData = instance.$element[0].files;
        const extensionsArr = extensions.split(',');

        /* filesData - это FileList, а не Array, так что привязываем every через call */
        const isCorrect = Array.prototype.every.call(filesData, fileData => extensionsArr.includes(fileData.type));

        return isCorrect;
    },
    messages: {
        ru: 'Допустимые расширения файлов: %s',
        en: 'Available extensions: %s',
    },
});

Parsley.addValidator('fileTypes', {
    /**
     * Проверяет на допустимость тип загружаемого файла.
     *
     * @param {string} value Название файла (не используется).
     * @param {string} types Допустимые типы файлов (группы MIME types).
     * @param {Object} instance Объект Parsley.
     * @return {boolean} Результат проверки.
     */
    validateString(value, types, instance) {
        const filesData = instance.$element[0].files;
        const typesArr = types.split(',');

        /* filesData - это FileList, а не Array, так что привязываем every через call */
        const isCorrect = Array.prototype.every.call(filesData, fileData =>
            typesArr.includes(fileData.type.split('/')[0])
        );

        return isCorrect;
    },
    messages: {
        ru: 'Допустимые типы файлов: %s',
        en: 'Available types: %s',
    },
});

$('form[data-validate="true"]').parsley();
